import { forwardRef, type HTMLAttributes } from "react";

export type StarRatingSize = "sm" | "md" | "lg" | "xl";

export interface StarRatingProps extends HTMLAttributes<HTMLDivElement> {
  /** Number of stars earned (0-3) */
  stars: 0 | 1 | 2 | 3;
  /** Maximum stars (always 3 for JumpBean) */
  maxStars?: 3;
  /** Size of the stars */
  size?: StarRatingSize;
  /** Whether to animate stars appearing */
  animated?: boolean;
  /** Whether this is showing the result of a just-completed challenge */
  isNewResult?: boolean;
}

/**
 * Star rating display for challenge results.
 * JumpBean uses a 1-3 star rating system for all challenges.
 *
 * @example
 * // Display earned stars
 * <StarRating stars={2} />
 *
 * @example
 * // Animated result after completing a challenge
 * <StarRating stars={3} size="lg" isNewResult animated />
 */
export const StarRating = forwardRef<HTMLDivElement, StarRatingProps>(
  (
    {
      stars,
      maxStars = 3,
      size = "md",
      animated = false,
      isNewResult = false,
      className = "",
      ...props
    },
    ref
  ) => {
    // Size configurations
    const sizeStyles = {
      sm: {
        star: "w-6 h-6",
        gap: "gap-1",
      },
      md: {
        star: "w-10 h-10",
        gap: "gap-2",
      },
      lg: {
        star: "w-14 h-14",
        gap: "gap-3",
      },
      xl: {
        star: "w-20 h-20",
        gap: "gap-4",
      },
    };

    const currentSize = sizeStyles[size];

    return (
      <div
        ref={ref}
        role="img"
        aria-label={`${stars} out of ${maxStars} stars`}
        className={`
          inline-flex items-center
          ${currentSize.gap}
          ${className}
        `}
        {...props}
      >
        {[...Array(maxStars)].map((_, index) => {
          const isFilled = index < stars;
          const animationDelay = animated ? index * 200 : 0;

          return (
            <div
              key={index}
              className={`
                relative
                ${currentSize.star}
                transition-all duration-300
                ${isFilled ? "scale-100" : "scale-90 opacity-40"}
                ${animated && isFilled ? "animate-[starPop_500ms_cubic-bezier(0.34,1.56,0.64,1)_forwards]" : ""}
              `}
              style={{
                animationDelay: `${animationDelay}ms`,
                animationFillMode: "both",
              }}
            >
              {/* Star base */}
              <svg
                viewBox="0 0 24 24"
                className="w-full h-full"
                aria-hidden="true"
              >
                {/* Shadow/outline */}
                <path
                  d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                  fill={isFilled ? "transparent" : "var(--color-neutral-300)"}
                  stroke={isFilled ? "var(--color-sunshine-700)" : "var(--color-neutral-400)"}
                  strokeWidth="1"
                />

                {/* Gradient fill for earned stars */}
                {isFilled && (
                  <>
                    <defs>
                      <linearGradient
                        id={`starGradient-${index}`}
                        x1="0%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor="var(--color-sunshine-400)" />
                        <stop offset="50%" stopColor="var(--color-sunshine-500)" />
                        <stop offset="100%" stopColor="var(--color-sunshine-600)" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                      fill={`url(#starGradient-${index})`}
                    />
                    {/* Inner shine */}
                    <path
                      d="M12 4l2.2 4.5 5 .7-3.6 3.5.85 5L12 15.5l-4.45 2.2.85-5-3.6-3.5 5-.7L12 4z"
                      fill="rgba(255,255,255,0.3)"
                    />
                  </>
                )}
              </svg>

              {/* Sparkle effects for new results */}
              {isNewResult && isFilled && (
                <>
                  <Sparkle
                    className="absolute -top-2 -right-1 w-3 h-3 text-sunshine-300"
                    delay={animationDelay + 300}
                  />
                  <Sparkle
                    className="absolute -bottom-1 -left-2 w-2 h-2 text-sunshine-400"
                    delay={animationDelay + 500}
                  />
                  <Sparkle
                    className="absolute top-0 left-0 w-2 h-2 text-white"
                    delay={animationDelay + 400}
                  />
                </>
              )}

              {/* Glow effect for filled stars */}
              {isFilled && (
                <div
                  className="
                    absolute inset-0
                    rounded-full
                    bg-sunshine-400/30
                    blur-md
                    -z-10
                    scale-75
                  "
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }
);

StarRating.displayName = "StarRating";

/** Animated sparkle decoration */
function Sparkle({
  className,
  delay = 0,
}: {
  className?: string;
  delay?: number;
}) {
  return (
    <svg
      className={`${className} animate-sparkle`}
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{ animationDelay: `${delay}ms` }}
      aria-hidden="true"
    >
      <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
    </svg>
  );
}

// Add star pop animation if not present
if (typeof document !== "undefined") {
  const styleId = "jumpbean-star-animations";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes starPop {
        0% {
          transform: scale(0);
          opacity: 0;
        }
        50% {
          transform: scale(1.3);
        }
        70% {
          transform: scale(0.9);
        }
        100% {
          transform: scale(1);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

export default StarRating;
